/**
 * The links Minka emails, as the SPA reads them back. See App.tsx for when
 * they are read and how the address bar is cleaned up afterwards.
 */

type LocationLike = Pick<Location, 'pathname' | 'hash'>;

export const INVITE_PATH = /^\/invite\/([^/?#]+)\/?$/;
export const RESET_PASSWORD_PATH = /^\/reset-password\/?$/;
export const CONFIRM_EMAIL_PATH = /^\/confirm-email\/?$/;

/** A mangled link must not take the whole app down with a URIError. */
function decode(value: string): string | null {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
}

/** `/invite/<token>` */
export function readInviteTokenFrom(location: LocationLike): string | null {
  const match = INVITE_PATH.exec(location.pathname);
  return match ? decode(match[1]) : null;
}

/**
 * `/reset-password#<token>`. The token rides in the fragment, which the
 * browser never sends: it stays out of every access log between the user and
 * the server, and out of Referer headers.
 */
export function readResetTokenFrom(location: LocationLike): string | null {
  return readFragmentToken(RESET_PASSWORD_PATH, location);
}

/**
 * `/confirm-email#<token>`, the link a self-service email change sends to the
 * new address. In the fragment for the same reasons as the reset token.
 */
export function readConfirmEmailTokenFrom(location: LocationLike): string | null {
  return readFragmentToken(CONFIRM_EMAIL_PATH, location);
}

function readFragmentToken(path: RegExp, location: LocationLike): string | null {
  if (!path.test(location.pathname)) return null;

  const token = location.hash.replace(/^#/, '');
  return token ? decode(token) : null;
}
